import { ChangeDetectionStrategy, Component, computed, Directive, ElementRef, inject, input } from '@angular/core';
import { cn } from '@mmscan/angular-ui/core';
import { cva, type VariantProps } from 'class-variance-authority';

export const cardVariants = cva('flex flex-col gap-3 rounded-lg p-4 text-surface-foreground', {
  variants: {
    variant: {
      surface: 'bg-surface',
      outline: 'border border-border bg-background text-foreground',
      elevated: 'bg-background text-foreground shadow-md',
    },
    interactive: {
      true: [
        'w-full cursor-pointer text-left transition-shadow duration-(--mmscan-duration-fast) hover:shadow-md',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
      ],
      false: '',
    },
  },
  defaultVariants: { variant: 'surface', interactive: false },
});

export type CardVariant = NonNullable<VariantProps<typeof cardVariants>['variant']>;

/**
 * Container for grouped content. Use `<a mmscanCard>` or `<button mmscanCard>` for a
 * clickable card — the native element provides keyboard and screen-reader behaviour.
 *
 * ```html
 * <mmscan-card>
 *   <div mmscanCardHeader>
 *     <h3 mmscanCardTitle>Open cases</h3>
 *     <p mmscanCardDescription>Last 30 days</p>
 *   </div>
 *   <div mmscanCardContent>…</div>
 *   <div mmscanCardFooter>…</div>
 * </mmscan-card>
 * ```
 */
@Component({
  selector: 'mmscan-card, a[mmscanCard], button[mmscanCard]',
  template: '<ng-content />',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'classes()' },
})
export class MmscanCard {
  private readonly tagName = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement.tagName;

  readonly variant = input<CardVariant>('surface');
  readonly class = input<string>('');

  protected readonly classes = computed(() =>
    cn(cardVariants({ variant: this.variant(), interactive: this.tagName !== 'MMSCAN-CARD' }), this.class())
  );
}

@Directive({ selector: '[mmscanCardHeader]', host: { class: 'flex flex-col gap-1' } })
export class MmscanCardHeader {}

@Directive({ selector: '[mmscanCardTitle]', host: { class: 'text-lg font-bold leading-tight' } })
export class MmscanCardTitle {}

@Directive({ selector: '[mmscanCardDescription]', host: { class: 'text-sm text-muted-foreground' } })
export class MmscanCardDescription {}

@Directive({ selector: '[mmscanCardContent]', host: { class: 'flex-1' } })
export class MmscanCardContent {}

@Directive({ selector: '[mmscanCardFooter]', host: { class: 'flex items-center gap-2' } })
export class MmscanCardFooter {}

/** Every card part, for `imports: [...MMSCAN_CARD]`. */
export const MMSCAN_CARD = [
  MmscanCard,
  MmscanCardHeader,
  MmscanCardTitle,
  MmscanCardDescription,
  MmscanCardContent,
  MmscanCardFooter,
] as const;
